import { useState, useCallback, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { loginSchema, type LoginFormValues } from '@dcp/shared'
import { useAuth } from '@/contexts/AuthContext'
import { MFAVerifyStep } from '@/components/auth/MFAVerifyStep'
import { PasswordField } from '@/components/ui/password-field'
import {
  AsyncButton,
  Input,
  Label,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Alert,
  AlertDescription,
} from '@atrvd/saas-ui'

/**
 * Login page with two-step flow:
 * 1. Email + password form
 * 2. TOTP verification (shown only when the user has MFA enabled)
 */
export function LoginPage() {
  const { signIn, signOut, completeMFALogin, isMFAPending } = useAuth()

  /**
   * Whether the MFA verification step should be shown instead of the login form.
   * Initialized from isMFAPending so that page refreshes during MFA go straight
   * to the TOTP input instead of showing the password form again.
   */
  const [showMFA, setShowMFA] = useState(isMFAPending)

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  /**
   * Handle credential submission.
   * signIn() checks AAL internally and returns requiresMFA if TOTP is enabled.
   * The user state is NOT set until MFA is verified, preventing AuthLayout redirect.
   */
  const onSubmit = async (data: LoginFormValues) => {
    const { error, requiresMFA } = await signIn(data.email, data.password)

    if (error) {
      setError('root', { message: error.message })
      return
    }

    if (requiresMFA) {
      setShowMFA(true)
    }
  }

  /** Ref for setting root error on the login form from MFA callbacks */
  const setErrorRef = useRef(setError)
  setErrorRef.current = setError

  /**
   * Called after successful MFA verification.
   * Finalizes login — re-checks org suspension, then sets user state.
   * If org is suspended, shows error and returns to login form.
   */
  const handleMFAVerified = useCallback(async () => {
    const { error } = await completeMFALogin()
    if (error) {
      setShowMFA(false)
      setErrorRef.current('root', { message: error.message })
    }
  }, [completeMFALogin])

  /**
   * Resets back to the login form when user clicks "Back to login".
   * Signs out the aal1 session so the user can start fresh.
   */
  const handleMFABack = useCallback(async () => {
    setShowMFA(false)
    await signOut()
  }, [signOut])

  // Show MFA verification step when the user has TOTP enabled
  if (showMFA) {
    return <MFAVerifyStep onVerified={handleMFAVerified} onBack={handleMFABack} />
  }

  return (
    <Card className="w-full">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold text-center">Sign in</CardTitle>
        <CardDescription className="text-center">
          Enter your credentials to access DCP
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {errors.root && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="name@company.com"
              autoComplete="email"
              {...register('email')}
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>

          <PasswordField
            id="password"
            label="Password"
            placeholder="Enter your password"
            autoComplete="current-password"
            error={errors.password?.message}
            {...register('password')}
          />

          <AsyncButton
            type="submit"
            className="w-full"
            isLoading={isSubmitting}
            loadingText="Signing in..."
          >
            Sign in
          </AsyncButton>
        </form>
      </CardContent>
    </Card>
  )
}